<template>
  <div class="creator-lite-view flex flex-col">
    <page-header
      :title="tr('Infinite∞Creator')"
      badge="Lite"
      :info="tr('Create AI-powered optimized content based on the specified goals and parameters. The artificial intelligence will automatically generate effective text and visuals that best align with your objectives.')"
    >
      <template #start>
        <creator-credit-counter />
      </template>
      <template #buttons>
        <ui-button as-child>
          <router-link to="/creator-lite/new">
            <sparkles-icon />
            {{ tr('Generate new content') }}
          </router-link>
        </ui-button>
      </template>
    </page-header>

    <div class="flex flex-col gap-4 py-5">
      <info-text
        v-if="hasNoActiveSubscription"
        type="warning"
        link="/settings/billing-and-payment"
        large
      >
        {{ tr('You currently do not have an active subscription!') }}
        {{ tr('To use Infinite∞Creator Lite, select and subscribe to the package that suits you best.') }}
      </info-text>

      <empty-screen
        v-if="!isLoading && list.length === 0"
        icon="generate-content"
        :title="tr('Create your first AI-generated social media content')"
        :flag-text="tr('Start the content creation')"
        :description="tr('Generate professional social media content powered by AI. Choose your content goal, target audience, and tone of voice. Our AI will create compelling copy and suggest matching visuals optimized for Facebook and Instagram engagement.')"
      >
        <template #under-description>
          <ui-button as-child class="self-start">
            <router-link to="/creator-lite/new">{{ tr('Generate new content') }}</router-link>
          </ui-button>
        </template>
      </empty-screen>

      <template v-else>
        <div role="table" :aria-label="tr('Generated contents')" class="flex flex-col gap-2">
          <div role="row" class="grid items-center gap-4 grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_8rem_2rem] px-4 text-xs font-semibold text-muted-foreground max-md:hidden">
            <span role="columnheader">{{ tr('Content') }}</span>
            <span role="columnheader">{{ tr('Generated') }}</span>
            <span role="columnheader" />
          </div>

          <div
            v-for="item in list"
            :key="item.id"
            role="row"
            :class="[
              'glass-tile flex flex-col gap-3 rounded-xl p-4 transition-shadow duration-300 ease-in-out hover:[--tile-glow:color-mix(in_oklab,var(--primary)_40%,transparent)]',
              item.hasError && 'border-destructive/40!',
            ]"
          >
            <div class="grid items-center gap-4 grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_8rem_2rem]">
              <div role="cell" class="min-w-0">
                <router-link :to="'/creator-lite/' + item.id" class="no-underline">
                  <inline-social-content :entity="item" />
                </router-link>
              </div>
              <div role="cell" class="text-sm tabular-nums max-md:hidden">
                {{ formatToYMD(item.created_at) }}
                <span class="block text-xs text-muted-foreground">{{ formatTime(item.created_at) }}</span>
              </div>
              <div role="cell">
                <arrow-link :to="'/creator-lite/' + item.id" :label="tr('Details')" />
              </div>
            </div>

            <div v-if="item.isWaiting || item.isInProgress" class="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
              <loader-circle-icon class="size-3.5 animate-spin" />
              {{ tr('Content generation in progress...') }}
            </div>
            <div v-else-if="item.hasError" class="flex items-center gap-2 text-xs font-semibold text-destructive">
              <triangle-alert-icon class="size-3.5" />
              {{ tr('An error occurred while generating the content') }}
            </div>
          </div>

          <template v-if="isLoading || hasMore">
            <div
              v-for="i in [ 1, 2 ]"
              :key="'creator-lite-loader-skeleton-' + i"
              ref="scrollPagerTrigger"
              role="row"
              class="glass-tile rounded-xl p-4"
            >
              <!-- The cells of a loaded row, with placeholders of the same size. -->
              <div class="grid items-center gap-4 grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_8rem_2rem]">
                <div role="cell" class="min-w-0">
                  <inline-social-content />
                </div>
                <div role="cell" class="flex flex-col max-md:hidden">
                  <span class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-20" /></span>
                  <span class="flex h-4 items-center"><ui-skeleton class="h-3 w-10" /></span>
                </div>
                <div role="cell">
                  <ui-skeleton class="size-8 rounded-full" />
                </div>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { LoaderCircle, Sparkles, TriangleAlert } from '@lucide/vue';

import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import CreatorLiteService from '@/services/CreatorLite.js';
import ArrowLink from '@/ui/components/ArrowLink.vue';
import CreatorCreditCounter from '@/ui/components/CreatorCreditCounter.vue';
import CreatorLiteForm from '@/ui/components/CreatorLiteForm.vue';
import CreatorLiteItemDetails from '@/ui/components/CreatorLiteItemDetails.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import InfoText from '@/ui/components/InfoText.vue';
import InlineSocialContent from '@/ui/components/InlineSocialContent.vue';
import PageHeader from '@/ui/components/PageHeader.vue';
import { formatTime, formatToYMD } from '@/utils/date';

export default {
  name: 'CreatorLiteView',
  components: {
    ArrowLink,
    CreatorCreditCounter,
    EmptyScreen,
    InfoText,
    InlineSocialContent,
    LoaderCircleIcon: LoaderCircle,
    SparklesIcon: Sparkles,
    PageHeader,
    TriangleAlertIcon: TriangleAlert,
  },
  mixins: [ ScrollPagerMixin ],
  data: () => ({
    fetchTimers: {},
  }),
  computed: {
    list() {
      return this.$store.getters['creatorLite/list'];
    },
    isLoading() {
      return this.$store.getters['creatorLite/isLoading'];
    },
    hasMore() {
      return this.$store.getters['creatorLite/hasMore'];
    },
    hasNoActiveSubscription() {
      return this.$root.currentCompany.current_subscription === null;
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(newId) {
        if (!this.$route.path.startsWith('/creator-lite')) {
          return;
        }

        if (newId) {
          this.openContentModal();
        }
        else {
          this.$fullscreenModal.closeAll();
        }
      },
    },
    '$route.name': {
      immediate: true,
      handler(newName, oldName) {
        if (newName === 'CreatorLiteViewCreateNew') {
          this.openForm();
        }
        else if (oldName === 'CreatorLiteViewCreateNew') {
          this.$fullscreenModal.closeAll();
        }
      },
    },
  },
  created() {
    this.loadData();
  },
  beforeUnmount() {
    this.destroyAllFetchTimers();
  },
  methods: {
    formatTime,
    formatToYMD,
    loadData() {
      if (this.isLoading || !this.hasMore) {
        return;
      }

      this.$store.dispatch('creatorLite/loadMoreData')
        .then(items => {
          items.forEach(item => {
            if (item.isWaiting || item.isInProgress) {
              this.initFetchTimer(item.id);
            }
          });

          return items;
        })
        .catch(err => this.error(err))
        .finally(() => {
          if (this.hasMore) {
            this.$nextTick(() => this.initScrollPager('scrollPagerTrigger', 'loadData'));
          }
        });
    },
    openForm() {
      const modal = this.$fullscreenModal.open(
        this,
        CreatorLiteForm,
        { onCreated: entity => this.initFetchTimer(entity.id) },
        { closeWithOutclick: false },
      );

      if (modal) {
        modal.$on('closed', () => {
          if (this.$route.name === 'CreatorLiteViewCreateNew') {
            this.$router.push('/creator-lite');
          }
        });
      }
    },
    initFetchTimer(id) {
      if (id in this.fetchTimers) {
        return;
      }

      this.fetchTimers[id] = setInterval(() => {
        CreatorLiteService.get(id)
          .then(entity => {
            this.$store.dispatch('creatorLite/updateItem', entity);

            if (entity.isCompleted) {
              clearInterval(this.fetchTimers[id]);
              delete this.fetchTimers[id];
            }

            return entity;
          })
          .catch(err => {
            clearInterval(this.fetchTimers[id]);
            delete this.fetchTimers[id];
            this.error(err);
          });
      }, 10000);
    },
    destroyAllFetchTimers() {
      Object.keys(this.fetchTimers).forEach(id => {
        clearInterval(this.fetchTimers[id]);
        delete this.fetchTimers[id];
      });
    },

    openContentModal() {
      const modal = this.$fullscreenModal.open(
        this,
        CreatorLiteItemDetails,
        {},
        { closeWithOutclick: false },
      );

      if (modal) {
        modal.$on('closed', () => {
          if (this.$route.name === 'CreatorLiteDetailsView') {
            this.$router.push('/creator-lite');
          }
        });
      }
    },
  },
};
</script>
